import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { PrioritySettings } from '@/components/priority/PrioritySelector';
import { usePriorities } from '@/hooks/use-priorities';

interface PrioritySettingsContextType {
  priorities: PrioritySettings;
  loading: boolean;
  error: string | null;
  updatePriorities: (newPriorities: PrioritySettings) => Promise<void>;
  updatePriority: (area: keyof PrioritySettings, priority: 'high' | 'medium' | 'low') => Promise<void>;
  resetPriorities: () => Promise<void>;
  getPriorityCount: (level: 'high' | 'medium' | 'low') => number;
  getHighPriorityAreas: () => string[];
  getMediumPriorityAreas: () => string[];
  getLowPriorityAreas: () => string[];
}

const PrioritySettingsContext = createContext<PrioritySettingsContextType | null>(null);

export function PrioritySettingsProvider({ children }: { children: ReactNode }) {
  const {
    priorities,
    loading,
    error,
    updatePriorities,
    updatePriority,
    resetPriorities
  } = usePriorities();

  const getPriorityCount = (level: 'high' | 'medium' | 'low') => {
    return Object.values(priorities).filter(p => p === level).length;
  };

  const getHighPriorityAreas = () => {
    return Object.entries(priorities)
      .filter(([_, priority]) => priority === 'high')
      .map(([key, _]) => key);
  };

  const getMediumPriorityAreas = () => {
    return Object.entries(priorities)
      .filter(([_, priority]) => priority === 'medium')
      .map(([key, _]) => key);
  };

  const getLowPriorityAreas = () => {
    return Object.entries(priorities)
      .filter(([_, priority]) => priority === 'low')
      .map(([key, _]) => key);
  };

  const value: PrioritySettingsContextType = {
    priorities,
    loading,
    error,
    updatePriorities,
    updatePriority,
    resetPriorities,
    getPriorityCount,
    getHighPriorityAreas,
    getMediumPriorityAreas,
    getLowPriorityAreas
  };

  return (
    <PrioritySettingsContext.Provider value={value}>
      {children}
    </PrioritySettingsContext.Provider>
  );
}

export function usePrioritySettings() {
  const context = useContext(PrioritySettingsContext);
  if (!context) {
    throw new Error('usePrioritySettings must be used within a PrioritySettingsProvider');
  }
  return context;
} 